import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  Chip,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  BugReport as BugIcon,
  Lightbulb as FeatureIcon,
  Info as GeneralIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  Comment as CommentIcon,
  CheckCircle as ResolvedIcon,
  Schedule as InProgressIcon,
  RadioButtonUnchecked as OpenIcon,
  Close as ClosedIcon,
  Star as PriorityHighIcon,
  StarBorder as PriorityMediumIcon,
  StarOutline as PriorityLowIcon,
  Reply as ReplyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { useUser } from '../contexts/UserContext';
import { FeedbackService, FeedbackFilters } from '../services/feedbackService';
import { Feedback, FeedbackThread } from '../types';
import { auth } from '../config/firebase';

const FeedbackPage: React.FC = () => {
  const { user, profile } = useUser();
  const [loading, setLoading] = useState(true);
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'bug' | 'feature' | 'general'>('all');
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [feedbackThreads, setFeedbackThreads] = useState<FeedbackThread[]>([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state for new feedback
  const [newFeedback, setNewFeedback] = useState({
    category: 'general' as 'bug' | 'feature' | 'general',
    title: '',
    description: '',
  });

  const statusTabs = ['all', 'open', 'in-progress', 'resolved', 'closed'];

  useEffect(() => {
    loadFeedback();
  }, [selectedTab, categoryFilter]);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      const filters: FeedbackFilters = {
        status: statusTabs[selectedTab] as any,
        category: categoryFilter,
        sortBy: 'recent',
      };
      const feedback = await FeedbackService.getFeedback(filters);
      setFeedbackList(feedback);
    } catch (error) {
      console.error('Error loading feedback:', error);
      setError('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!user || !newFeedback.title || !newFeedback.description) return;

    try {
      setSubmitting(true);
      setError(null);
      
      await FeedbackService.submitFeedback(
        user.uid,
        user.email || '',
        profile,
        newFeedback
      );

      // Reset form
      setNewFeedback({
        category: 'general',
        title: '',
        description: '',
      });
      setSubmitDialogOpen(false);
      
      // Reload feedback list
      await loadFeedback();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvoteFeedback = async (feedback: Feedback) => {
    if (!user) return;

    try {
      if (FeedbackService.hasUserUpvoted(feedback, user.uid)) {
        return; // Already upvoted
      }

      await FeedbackService.upvoteFeedback(feedback.id, user.uid);
      await loadFeedback();
    } catch (error) {
      console.error('Error upvoting feedback:', error);
    }
  };

  const handleViewDetails = async (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setDetailDialogOpen(true);
    
    try {
      const { threads } = await FeedbackService.getFeedbackDetail(feedback.id);
      setFeedbackThreads(threads);
    } catch (error) {
      console.error('Error loading feedback details:', error);
    }
  };

  const handleAddReply = async () => {
    if (!user || !selectedFeedback || !replyText.trim()) return;

    try {
      setSubmitting(true);
      
      await FeedbackService.addReply(
        selectedFeedback.id,
        user.uid,
        user.email || '',
        profile,
        replyText,
        profile?.isAdmin || false
      );

      setReplyText('');
      
      // Reload threads
      const { threads } = await FeedbackService.getFeedbackDetail(selectedFeedback.id);
      setFeedbackThreads(threads);
      
      // Update thread count in list
      await loadFeedback();
    } catch (error) {
      console.error('Error adding reply:', error);
      setError('Failed to add reply');
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'bug':
        return <BugIcon />;
      case 'feature':
        return <FeatureIcon />;
      default:
        return <GeneralIcon />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <OpenIcon />;
      case 'in-progress':
        return <InProgressIcon />;
      case 'resolved':
        return <ResolvedIcon />;
      case 'closed':
        return <ClosedIcon />;
      default:
        return <OpenIcon />;
    }
  };

  const getPriorityIcon = (priority?: string) => {
    switch (priority) {
      case 'high':
        return <PriorityHighIcon color="error" />;
      case 'medium':
        return <PriorityMediumIcon color="warning" />;
      case 'low':
        return <PriorityLowIcon />;
      default:
        return null;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'bug':
        return 'error';
      case 'feature':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'info';
      case 'in-progress':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Feedback & Suggestions
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setSubmitDialogOpen(true)}
        >
          Submit Feedback
        </Button>
      </Stack>

      {/* Filters */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, value) => setSelectedTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All" />
          <Tab label="Open" />
          <Tab label="In Progress" />
          <Tab label="Resolved" />
          <Tab label="Closed" />
        </Tabs>
        
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={1}>
            <Chip
              label="All Categories"
              onClick={() => setCategoryFilter('all')}
              color={categoryFilter === 'all' ? 'primary' : 'default'}
            />
            <Chip
              icon={<BugIcon />}
              label="Bugs"
              onClick={() => setCategoryFilter('bug')}
              color={categoryFilter === 'bug' ? 'error' : 'default'}
            />
            <Chip
              icon={<FeatureIcon />}
              label="Features"
              onClick={() => setCategoryFilter('feature')}
              color={categoryFilter === 'feature' ? 'primary' : 'default'}
            />
            <Chip
              icon={<GeneralIcon />}
              label="General"
              onClick={() => setCategoryFilter('general')}
              color={categoryFilter === 'general' ? 'default' : 'primary'}
            />
          </Stack>
        </Box>
      </Paper>

      {/* Error message */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Feedback list */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : feedbackList.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No feedback found
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Be the first to submit feedback!
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {feedbackList.map((feedback) => (
            <Card key={feedback.id}>
              <CardContent>
                <Stack spacing={2}>
                  {/* Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar src={feedback.userProfilePic} sx={{ width: 40, height: 40 }}>
                        {feedback.userDisplayName?.[0] || feedback.userEmail[0]}
                      </Avatar>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2">
                            {feedback.userDisplayName || feedback.userEmail}
                          </Typography>
                          {feedback.userHandle && (
                            <Typography variant="caption" color="text.secondary">
                              @{feedback.userHandle}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            · {formatDistanceToNow(feedback.createdAt, { addSuffix: true })}
                          </Typography>
                        </Stack>
                        <Typography variant="h6">{feedback.title}</Typography>
                      </Box>
                    </Stack>
                    
                    <Stack direction="row" spacing={1}>
                      {feedback.priority && getPriorityIcon(feedback.priority)}
                      <Chip
                        icon={getCategoryIcon(feedback.category)}
                        label={feedback.category}
                        size="small"
                        color={getCategoryColor(feedback.category) as any}
                      />
                      <Chip
                        icon={getStatusIcon(feedback.status)}
                        label={feedback.status.replace('-', ' ')}
                        size="small"
                        color={getStatusColor(feedback.status) as any}
                      />
                    </Stack>
                  </Stack>

                  {/* Description */}
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {feedback.description.length > 200
                      ? `${feedback.description.substring(0, 200)}...`
                      : feedback.description}
                  </Typography>

                  {/* Admin response preview */}
                  {feedback.adminResponse && (
                    <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Admin Response
                      </Typography>
                      <Typography variant="body2">
                        {feedback.adminResponse.substring(0, 100)}...
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </CardContent>
              
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <Button
                    size="small"
                    startIcon={
                      user && FeedbackService.hasUserUpvoted(feedback, user.uid)
                        ? <ThumbUpIcon />
                        : <ThumbUpOutlinedIcon />
                    }
                    onClick={() => handleUpvoteFeedback(feedback)}
                    disabled={!user || (user && FeedbackService.hasUserUpvoted(feedback, user.uid))}
                  >
                    {feedback.upvotes}
                  </Button>
                  
                  <Button
                    size="small"
                    startIcon={<CommentIcon />}
                    onClick={() => handleViewDetails(feedback)}
                  >
                    {feedback.threadCount} Replies
                  </Button>
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <Button
                    size="small"
                    onClick={() => handleViewDetails(feedback)}
                  >
                    View Details
                  </Button>
                </Stack>
              </CardActions>
            </Card>
          ))}
        </Stack>
      )}

      {/* Submit Feedback Dialog */}
      <Dialog
        open={submitDialogOpen}
        onClose={() => setSubmitDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit New Feedback</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newFeedback.category}
                onChange={(e) => setNewFeedback({ ...newFeedback, category: e.target.value as any })}
                label="Category"
              >
                <MenuItem value="bug">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BugIcon />
                    <span>Bug Report</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="feature">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FeatureIcon />
                    <span>Feature Request</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="general">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GeneralIcon />
                    <span>General Feedback</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Title"
              fullWidth
              value={newFeedback.title}
              onChange={(e) => setNewFeedback({ ...newFeedback, title: e.target.value })}
              placeholder="Brief summary of your feedback"
            />
            
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={6}
              value={newFeedback.description}
              onChange={(e) => setNewFeedback({ ...newFeedback, description: e.target.value })}
              placeholder="Provide detailed information about your feedback..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitFeedback}
            variant="contained"
            disabled={!newFeedback.title || !newFeedback.description || submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedFeedback && (
          <>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{selectedFeedback.title}</Typography>
                <IconButton onClick={() => setDetailDialogOpen(false)}>
                  <ClosedIcon />
                </IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={3}>
                {/* Feedback details */}
                <Box>
                  <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <Avatar src={selectedFeedback.userProfilePic}>
                      {selectedFeedback.userDisplayName?.[0] || selectedFeedback.userEmail[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        {selectedFeedback.userDisplayName || selectedFeedback.userEmail}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(selectedFeedback.createdAt, 'PPP')}
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Stack direction="row" spacing={1}>
                      <Chip
                        icon={getCategoryIcon(selectedFeedback.category)}
                        label={selectedFeedback.category}
                        color={getCategoryColor(selectedFeedback.category) as any}
                      />
                      <Chip
                        icon={getStatusIcon(selectedFeedback.status)}
                        label={selectedFeedback.status.replace('-', ' ')}
                        color={getStatusColor(selectedFeedback.status) as any}
                      />
                    </Stack>
                  </Stack>
                  
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedFeedback.description}
                  </Typography>
                </Box>

                {/* Admin response */}
                {selectedFeedback.adminResponse && (
                  <Paper sx={{ p: 2, bgcolor: 'primary.50' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Admin Response
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedFeedback.adminResponse}
                    </Typography>
                    {selectedFeedback.adminRespondedAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {format(selectedFeedback.adminRespondedAt, 'PPP')}
                      </Typography>
                    )}
                  </Paper>
                )}

                <Divider />

                {/* Threads */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Discussion ({feedbackThreads.length})
                  </Typography>
                  
                  {feedbackThreads.length > 0 ? (
                    <List>
                      {feedbackThreads.map((thread) => (
                        <ListItem key={thread.id} alignItems="flex-start" sx={{ px: 0 }}>
                          <ListItemAvatar>
                            <Avatar src={thread.userProfilePic}>
                              {thread.userDisplayName?.[0] || thread.userEmail[0]}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle2">
                                  {thread.userDisplayName || thread.userEmail}
                                </Typography>
                                {thread.isAdminReply && (
                                  <Chip label="Admin" size="small" color="primary" />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  · {formatDistanceToNow(thread.createdAt, { addSuffix: true })}
                                </Typography>
                              </Stack>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                                {thread.content}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No replies yet. Be the first to comment!
                    </Typography>
                  )}
                </Box>

                {/* Reply input */}
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add a reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<ReplyIcon />}
                      onClick={handleAddReply}
                      disabled={!replyText.trim() || submitting}
                    >
                      {submitting ? <CircularProgress size={24} /> : 'Reply'}
                    </Button>
                  </Box>
                </Box>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default FeedbackPage;